// Dev/demo-only illustrative data for the Analytics screens (for screenshots/walkthroughs).
// This is NEVER used in normal runs: it is only returned when REACT_APP_ANALYTICS_DEMO === 'true'
// or window.__PONS_DEMO_MODE === true (see EnhancedReportsPage.fetchData).
//
// The story is OUTCOME-based: what the firm actually gets from PONS.
//   - Time saved (hours) and the money value of that time, with the "why" (per-workflow breakdown)
//   - Efficiency gain and ROI vs PONS cost
//   - What is used most, and who is most active
// Figures reconcile with the platform time-savings benchmarks:
//   value of time saved = hours saved x EUR 150 blended billable rate.
import { ChartDataInterface } from './EnhancedAnalyticsCharts';

type TrendStatus = 'increase' | 'decrease' | 'neutral';

export interface DemoAnalyticsPayload {
  static_fields: Array<{
    key: string;
    label: string;
    total: number | string;
    trend?: { trend_percent: number; trend_status: TrendStatus };
  }>;
  barChartData: ChartDataInterface[];
  shortChartData: ChartDataInterface[];
  chartData: ChartDataInterface[];
}

const BLUES = ['#1E3A8A', '#2563EB', '#3B82F6', '#60A5FA', '#93C5FD', '#BFDBFE'];
const GREENS = ['#065F46', '#059669', '#10B981', '#34D399', '#6EE7B7', '#A7F3D0'];
const STATUS = ['#10B981', '#F59E0B', '#EF4444']; // good / watch / bad

// Build the last N calendar days as ISO date strings (granularity the page expects).
const lastNDates = (n: number): string[] => {
  const out: string[] = [];
  const today = new Date();
  for (let i = n - 1; i >= 0; i--) {
    const d = new Date(today);
    d.setDate(today.getDate() - i);
    out.push(d.toISOString().slice(0, 10));
  }
  return out;
};

// Deterministic, organic-looking series (no randomness, so screenshots are stable).
const WOBBLE = [0, 2, -1, 3, -2, 1, 4, -1, 2, -3, 3, 0, -2, 2];
const series = (len: number, base: number, growth: number, scale = 1): number[] =>
  Array.from({ length: len }, (_, i) =>
    Math.max(0, Math.round(base + growth * i + WOBBLE[i % WOBBLE.length] * scale))
  );
const cumulative = (arr: number[]): number[] => {
  let s = 0;
  return arr.map((v) => (s += v));
};

const DATES30 = lastNDates(30);

// Per-workflow time saved (hrs) this quarter; sums to ~1,548 hrs (matches the assessment).
const WORKFLOWS = ['Batch DD', 'Matter intake', 'Contract review', 'Doc generation', 'DPA/GDPR', 'Repaper'];
const HRS_BY_WF = [468, 360, 360, 150, 120, 90];
const RATE = 150; // EUR blended billable
const EUR_BY_WF = HRS_BY_WF.map((h) => h * RATE); // value created per workflow

export const getDemoAnalytics = (tab: string): DemoAnalyticsPayload => {
  const isOrg = tab === 'organization';

  // ── Outcome KPI cards ────────────────────────────────────────────────
  const static_fields: DemoAnalyticsPayload['static_fields'] = isOrg
    ? [
        { key: 'avg_matter_duration', label: 'Time Saved (hrs)', total: 1548, trend: { trend_percent: 15, trend_status: 'increase' } },
        { key: 'revenue', label: 'Value of Time Saved', total: '€232k', trend: { trend_percent: 15, trend_status: 'increase' } },
        { key: 'clients_per_lawyer', label: 'PONS ROI', total: '32×' },
        { key: 'avg_turnaround', label: 'Avg Efficiency Gain', total: '84%', trend: { trend_percent: 4, trend_status: 'increase' } },
        { key: 'avg_baseline_creation', label: 'Hrs Saved / Lawyer / Wk', total: 10, trend: { trend_percent: 12, trend_status: 'increase' } },
        { key: 'documents_analyzed', label: 'Documents Processed', total: 4812, trend: { trend_percent: 23, trend_status: 'increase' } },
        { key: 'team_members', label: 'Most Active User', total: 'M. Berg' },
        { key: 'ai_processed', label: 'Most-Used Tool', total: 'Research' },
      ]
    : [
        { key: 'avg_matter_duration', label: 'Time Saved (hrs)', total: 129, trend: { trend_percent: 12, trend_status: 'increase' } },
        { key: 'revenue', label: 'Value of My Time Saved', total: '€19k', trend: { trend_percent: 12, trend_status: 'increase' } },
        { key: 'avg_turnaround', label: 'My Efficiency Gain', total: '82%', trend: { trend_percent: 5, trend_status: 'increase' } },
        { key: 'drafts', label: 'Drafts Generated', total: 41, trend: { trend_percent: 22, trend_status: 'increase' } },
        { key: 'research_queries', label: 'Research Queries', total: 86, trend: { trend_percent: 31, trend_status: 'increase' } },
        { key: 'ai_processed', label: 'Most-Used Tool', total: 'Reviews' },
      ];

  const m = isOrg ? 1 : 0.3; // scale personal down relative to org

  // ── Activity Trends (time-series bars, show momentum) ────────────────
  const barChartData: ChartDataInterface[] = [
    { chart_type: 'bar', chart_title: 'Documents Processed', x_values: DATES30, y_values: series(30, isOrg ? 90 : 24, 3.0, m * 4), color_codes: [BLUES[1]] },
    { chart_type: 'bar', chart_title: 'Reviews Run', x_values: DATES30, y_values: series(30, isOrg ? 8 : 3, 0.25, m), color_codes: [BLUES[0]] },
    { chart_type: 'bar', chart_title: 'Drafts Generated', x_values: DATES30, y_values: series(30, isOrg ? 6 : 2, 0.2, m), color_codes: [BLUES[2]] },
  ];

  // ── Quick Insights (gauges + what-is-used-most doughnuts) ────────────
  const shortChartData: ChartDataInterface[] = [
    { chart_type: 'gauge', chart_title: 'Team Utilization', x_values: ['Utilization'], y_values: [isOrg ? 78 : 72] },
    { chart_type: 'gauge', chart_title: 'On-Time Task Rate', x_values: ['On-time'], y_values: [isOrg ? 88 : 90] },
    {
      chart_type: 'doughnut',
      chart_title: 'Most-Used Tools',
      x_values: ['Research', 'Reviews', 'Drafting', 'Tables', 'Templates'],
      y_values: isOrg ? [540, 412, 296, 168, 84] : [86, 71, 41, 12, 9],
      color_codes: BLUES,
    },
    {
      chart_type: 'doughnut',
      chart_title: 'Review Outcomes',
      x_values: ['Compliant', 'Needs work', 'Non-compliant'],
      y_values: isOrg ? [68, 22, 10] : [70, 20, 10],
      color_codes: [STATUS[0], STATUS[1], STATUS[2]],
    },
  ];

  // ── Detailed Analytics (the outcome breakdowns: time, money, who/what) ─
  const chartData: ChartDataInterface[] = [
    {
      chart_type: 'line',
      chart_title: 'Cumulative Time Saved (hrs)',
      x_values: DATES30,
      datasets: [
        {
          label: 'Hours saved (running total)',
          data: cumulative(series(30, isOrg ? 35 : 3, isOrg ? 1.2 : 0.12, m * 3)),
          borderColor: BLUES[1],
          backgroundColor: 'rgba(37,99,235,0.12)',
          fill: true,
          tension: 0.4,
        },
      ],
    },
    {
      chart_type: 'bar',
      chart_title: 'Time Saved by Workflow (hrs)',
      x_values: WORKFLOWS,
      y_values: isOrg ? HRS_BY_WF : HRS_BY_WF.map((h) => Math.round(h * 0.08)),
      color_codes: BLUES,
    },
    {
      chart_type: 'bar',
      chart_title: 'Value Created by Workflow (€)',
      x_values: WORKFLOWS,
      y_values: isOrg ? EUR_BY_WF : EUR_BY_WF.map((v) => Math.round(v * 0.08)),
      color_codes: GREENS,
    },
    ...(isOrg
      ? [
          {
            chart_type: 'bar' as const,
            chart_title: 'Most Active Users (actions)',
            x_values: ['M. Berg', 'A. Nilsson', 'K. Haugen', 'S. Lind', 'T. Dahl', 'E. Voss'],
            y_values: [142, 128, 119, 96, 88, 74],
            color_codes: BLUES,
          },
        ]
      : []),
    {
      chart_type: 'pie',
      chart_title: 'Work by Practice Area',
      x_values: ['Corporate', 'Compliance', 'Litigation', 'Employment', 'Real Estate'],
      y_values: [32, 26, 18, 14, 10],
      color_codes: BLUES,
    },
  ];

  return { static_fields, barChartData, shortChartData, chartData };
};
